import React from 'react';
import { t } from 'i18next';
import moment from 'moment';
import { HiOutlineEye, HiDotsVertical, HiCheck } from 'react-icons/hi';
import { RadixDropdown, RadixSpinner } from '@/components/radix';
import { cnMerge } from '@/utils/cnMerge';
import type { GetApiDoctorsPortalMessages200MessagesItem } from '@/shared/api/models';
import { getPriorityStyles, getPriorityLabel } from './messagesViewUtils';

export interface CardLayoutProps {
    messages: GetApiDoctorsPortalMessages200MessagesItem[];
    selectedDoctorId: string;
    openPreview: (msg: GetApiDoctorsPortalMessages200MessagesItem) => void;
    handleMarkSingleRead: (id: string) => void;
    markReadMutation: {
        mutate: (ids: string[]) => void;
        isPending: boolean;
        variables?: string[];
    };
}

export default function CardLayout({
    messages,
    selectedDoctorId,
    openPreview,
    handleMarkSingleRead,
    markReadMutation,
}: CardLayoutProps) {
    const DOCTOR_ALL_VALUE = '__all__';

    return (
        <div className="grid grid-cols-1 gap-4">
            {messages.map((msg) => {
                const styles = getPriorityStyles(msg.priority);
                const isMarkingThis =
                    markReadMutation.isPending &&
                    Array.isArray(markReadMutation.variables) &&
                    markReadMutation.variables.length === 1 &&
                    markReadMutation.variables[0] === msg.id;
                return (
                    <div
                        key={msg.id}
                        className={cnMerge(
                            'rounded-md border-l-4 p-4 relative',
                            styles.bg,
                            styles.borderLeft,
                            msg.readAt ? 'border-0' : 'border',
                            !msg.readAt && styles.border,
                        )}
                    >
                        <div className="flex flex-col gap-2">
                            <div className="flex w-full flex-wrap items-start justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                                    {!msg.readAt && <span className=" w-3 h-3 rounded-full bg-orange-500 shrink-0" />}
                                    <h3 className="text-base font-semibold text-text-primary m-0">{msg.title}</h3>
                                </div>
                                <RadixDropdown
                                    trigger={
                                        <button
                                            type="button"
                                            className="bg-transparent p-1 focus:outline-none focus:ring-0 text-text-secondary border-none cursor-pointer rounded-sm ml-auto"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <HiDotsVertical size={18} />
                                        </button>
                                    }
                                    items={
                                        [
                                            {
                                                label: (
                                                    <span className="flex items-center gap-2">
                                                        <HiOutlineEye className="w-4 h-4" />
                                                        {t('Doctors.ViewMessage')}
                                                    </span>
                                                ),
                                                onClick: () => openPreview(msg),
                                            },
                                            !msg.readAt
                                                ? {
                                                      label: isMarkingThis ? (
                                                          <span className="flex items-center gap-2">
                                                              <RadixSpinner size="sm" variant="primary" />
                                                              {t('Doctors.MarkingMessagesRead')}
                                                          </span>
                                                      ) : (
                                                          <span className="flex items-center gap-2">
                                                              <HiCheck className="w-4 h-4" />
                                                              {t('Doctors.MarkMessageRead')}
                                                          </span>
                                                      ),
                                                      onClick: () => handleMarkSingleRead(msg.id),
                                                      disabled: markReadMutation.isPending,
                                                  }
                                                : undefined,
                                        ].filter(Boolean) as any
                                    }
                                    align="end"
                                />
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-secondary">
                                {selectedDoctorId === DOCTOR_ALL_VALUE && msg.doctor && (
                                    <span>
                                        {msg.doctor.firstName} {msg.doctor.lastName}
                                    </span>
                                )}
                                {msg.validUntil && (
                                    <span>
                                        {t('Doctors.ValidUntil')}: {moment(msg.validUntil).format('DD/MM-YYYY HH:mm')}
                                    </span>
                                )}
                                {msg.readAt && (
                                    <span>
                                        {t('Doctors.ReadAt')}: {moment(msg.readAt).format('DD/MM-YYYY HH:mm')}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2 pt-1 text-xs">
                                <span
                                    className={cnMerge(
                                        'inline-flex items-center px-2 py-0.5 rounded-full font-medium',
                                        styles.badge,
                                    )}
                                >
                                    {getPriorityLabel(msg.priority)}
                                </span>
                                {msg.isPinned && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-200">
                                        {t('Doctors.Pinned')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
